import type { FileRouteTypes } from "../routeTree.gen";
import type { IconName } from "./icons";

export type RoutePath = FileRouteTypes["to"];
export type SectionKey = "home" | "trials" | "season" | "genealogy" | "genebank" | "lab" | "analysis" | "system";

/** ``alias``: a shortcut to a screen another section owns (shown in the panel, never selects this section). */
export type NavItem = { id: string; to: RoutePath; keywords?: string; alias?: boolean };
/** ``bottom``: the section sits at the bottom of the icon rail. */
export type NavSection = { key: SectionKey; icon: IconName; items: NavItem[]; bottom?: boolean };

/** The two-level navigation: the icon rail (sections) and the side panel (screens of the section).
 *  Labels live in the catalogues (i18n/hu.ts, en.ts) under nav.<section>.<id>; keywords feed the quick search and
 *  are language-neutral: the Hungarian and the English terms, so either language finds the screen. */
const HOME: NavSection = {
  key: "home",
  icon: "home",
  items: [
    { id: "overview", to: "/", keywords: "kezdőlap tenyészidőszak home overview season cycle" },
    { id: "jobs", to: "/system/jobs", alias: true },
  ],
};

export const SECTIONS: NavSection[] = [
  HOME,
  {
    key: "trials",
    icon: "grid",
    items: [
      {
        id: "data",
        to: "/trials/data",
        keywords: "mérés termés szf min nir táblázat manual input trial data measurement yield table grid",
      },
      {
        id: "queries",
        to: "/trials/queries",
        keywords: "lekérdezés szűrés query filter excel export mentett queries power query saved query",
      },
      {
        id: "yearlyRetriever",
        to: "/trials/yearly-retriever",
        keywords: "előző év ekis eparc evi vi előzmény retriever termőhely átlag yearly retriever previous year history site mean",
      },
      {
        id: "dataInput",
        to: "/trials/data-input",
        keywords:
          "mérőállomás vonalkód mérleg mettler minolta esésszám műszer terepi telefon data input station barcode scale falling number instrument field mode phone",
      },
      {
        id: "formulas",
        to: "/trials/formulas",
        keywords: "képlet formula számítás osztályozás minőségi osztály átlag formulas calculation classification quality class mean",
      },
      {
        id: "import",
        to: "/trials/import",
        keywords:
          "import nir im9520 farinográf extenzográf alveográf ta.xt marvin skcs polycorder ht630 excel csv betöltés data import import from file farinograph extensograph alveograph load",
      },
    ],
  },
  {
    key: "season",
    icon: "sprout",
    items: [
      {
        id: "selection",
        to: "/season/selection",
        keywords:
          "szelekció selection kiválasztás következő év célkísérlet felh mag súlyhatár tkw testvér next year target trial seed weight limit sister",
      },
      {
        id: "crossing",
        to: "/season/crossing",
        keywords:
          "keresztezés crossing anya apa pedigré purdy kombináció paz keresztezési kód lista cross female male pedigree combination cross code list",
      },
      {
        id: "plotNumbering",
        to: "/season/plot-numbering",
        keywords:
          "parcellaszámozás plot numbering ideiglenes 900000 standard stnb stnc b típus c típus párhuzamos lr sr fuz új sid temporary check b-type c-type parallel new sid renumbering",
      },
      {
        id: "parallelExperiments",
        to: "/season/parallel-experiments",
        keywords: "párhuzamos kísérletek parallel experiments lr sr fuz betegségkert másolat kód parallel trials disease nursery copy code",
      },
      {
        id: "fieldBooks",
        to: "/season/field-books",
        keywords:
          "napló field book tenyészkerti a fb ta általános vetési lista vetésjegyzék súly blokk vetőgép tkw pdf excel nyomtatás field books sowing list general weight block seeder print",
      },
    ],
  },
  {
    key: "genealogy",
    icon: "tree",
    items: [
      {
        id: "genotype",
        to: "/genealogy/genotype",
        keywords:
          "genotípus adatlap törzs fajta paz sid 360 hol található génbank magposta genotype sheet line variety where found genebank seed post",
      },
      {
        id: "register",
        to: "/genealogy/register",
        keywords:
          "törzsregiszter pedigré kombináció fajta törzsnév rövidítés paz sid új kombináció pedigree management line register combination variety line name abbreviation new combination",
      },
      {
        id: "import",
        to: "/genealogy/import",
        keywords:
          "genealógiai import data import tömeges felvétel pedigré lista excel törzsregiszter új anyag parcellák genealogy import bulk pedigree list line register new material plots",
      },
      {
        id: "tree",
        to: "/genealogy/tree",
        keywords: "pedigréfa tree felmenő ancestry részesedés gén anya apa pedigree tree ancestor share gene female male",
      },
      {
        id: "newSid",
        to: "/genealogy/new-sid",
        keywords:
          "új sid testvérvonal vonalnév névadás pedigré szekvencia csere pedakt megjelenített név new sid sister line line names naming pedigree sequence change display name",
      },
      {
        id: "merge",
        to: "/genealogy/merge",
        keywords:
          "összevonás merge duplikátum egyesítés azonosító átszámozás transpose lista paz sid simple merge duplicates renumbering list",
      },
      {
        id: "consistency",
        to: "/genealogy/consistency",
        keywords:
          "törzsek ellenőrzése purge paz sid konzisztencia nem használt törzs csonk regisztrálatlan kis parc line consistency check unused line stub unregistered",
      },
      {
        id: "parentPerformance",
        to: "/genealogy/parent-performance",
        keywords:
          "szülők teljesítménye parent performance f2 a f4 b továbbvitt sorok szelekció utódok parents rows carried on selection offspring",
      },
      {
        id: "ancestors",
        to: "/genealogy/ancestors",
        keywords:
          "felmenők keresése genanal find parents pattern trait ős szülő nagyszülő gén hordozó törzslista ancestry search ancestor parent grandparent gene carrier line list",
      },
      {
        id: "geneSearch",
        to: "/genealogy/gene-search",
        keywords:
          "génkereső gén allél marker hordozó utód szülő lr sr rht molekuláris marker tulajdonság asszociáció gene search allele carrier offspring parent molecular marker trait association",
      },
    ],
  },
  {
    key: "genebank",
    icon: "box",
    items: [
      {
        id: "genebank",
        to: "/genebank/genebank",
        keywords:
          "génbank koll hűtő huto tároló zacskó betárolás kivétel felújítás ngbab genebank accession fridge storage bag intake withdrawal refresh",
      },
      {
        id: "shipments",
        to: "/genebank/shipments",
        keywords: "magküldés küldemény szállítólevél delivery note címke matrica novexx partner seed shipment seed post label sticker",
      },
      {
        id: "arrivals",
        to: "/genebank/arrivals",
        keywords: "magérkezés érkezés beérkezett vetőmag erk mta anyagátadás partner seed arrival arrived seed material transfer",
      },
      {
        id: "partners",
        to: "/genebank/partners",
        keywords:
          "partner címlista cím intézmény kapcsolattartó magcsere küldés érkezés address list partners institution contact seed exchange shipment arrival",
      },
    ],
  },
  {
    key: "lab",
    icon: "flask",
    items: [
      {
        id: "genetech",
        to: "/lab/genetech",
        keywords:
          "primer marker gén allél kombináció genetech molekuláris szinonima kapcsolat fenotípus allél-eredmény gene for phenotype primers markers genes allele combination molecular synonym link allele result",
      },
      {
        id: "dnaIsolation",
        to: "/lab/dna-isolation",
        keywords: "dns izolálás minta doboz 10x10 pozíció dnasource ctab dna isolation sample box position",
      },
      {
        id: "dnaDilution",
        to: "/lab/dna-dilution",
        keywords:
          "dns hígítás lemez 96 lyuk pipettázás koncentráció munkaoldat dna dilution plate well pipetting concentration working solution",
      },
    ],
  },
  {
    key: "analysis",
    icon: "chart",
    items: [
      {
        id: "anova",
        to: "/analysis/anova",
        keywords:
          "varianciaanalízis anova szd lsd cv blokk ismétlés termőhely évek osztott parcellás yates standard statisztika analysis of variance block replication site years split plot statistics",
      },
      {
        id: "relations",
        to: "/analysis/relations",
        keywords:
          "leíró statisztika átlag szórás regresszió korreláció összefüggés pearson szórásdiagram lekérdezés descriptive statistics mean standard deviation regression correlation relations scatter plot query",
      },
      {
        id: "labels",
        to: "/analysis/labels",
        keywords:
          "címke címkenyomtatás novexx easy plug vonalkód matrica a4 3651 3652 sablon véletlen sorrend kígyózó labels label printing sticker barcode template random order serpentine",
      },
    ],
  },
  {
    key: "system",
    icon: "sliders",
    bottom: true,
    items: [
      { id: "jobs", to: "/system/jobs", keywords: "export háttér feladat background jobs job" },
      {
        id: "journal",
        to: "/system/journal",
        keywords: "audit változásnapló visszavonás adatminőség journal operation log change log undo data quality",
      },
      {
        id: "settings",
        to: "/system/settings",
        keywords: "beállítás billentyű téma nyelv settings keyboard shortcuts theme language",
      },
    ],
  },
];

/** Every screen once (aliases skipped), with the section that owns it. */
export function allItems(): { section: NavSection; item: NavItem }[] {
  return SECTIONS.flatMap((section) => section.items.filter((item) => !item.alias).map((item) => ({ section, item })));
}

/** The section and screen a path belongs to: the longest matching screen path wins. */
export function sectionOf(pathname: string): { section: NavSection; item: NavItem | undefined } {
  const matches = allItems().filter(({ item }) =>
    item.to === "/" ? pathname === "/" : pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
  const best = matches.sort((a, b) => b.item.to.length - a.item.to.length)[0];
  return best ?? { section: HOME, item: undefined };
}
